import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { syncItemsToBackend as syncItemsToBackendUtil } from '../utils/backendSync';
import { parseMarkdownForCategory, ALL_MARKDOWN_FILES } from '../utils/parseAllMarkdown';
import { loadCategory as loadCategoryModule, getCategoryFromId, getCategoryKeys } from './items/categoryLoader';
import { dedupeCatalogItemsById } from '../utils/catalogDedupe';
import { filterCatalogItemsBySubFilter } from '../utils/catalogFilter';
import {
  Code, Smartphone, Database, Camera, User, Palette, Image, Shirt,
  Book, Newspaper, PenLine, SlidersHorizontal, GraduationCap, Users, Library, BookOpen,
  Utensils, Calendar, Building, Sprout, Gem, Hammer,
  UserCircle, Compass, Baby, Home, Lightbulb, Handshake,
  Dumbbell, Heart, Volleyball, Wallet, Flower, UsersRound, Globe, Film
} from 'lucide-react';
import { useAccountData } from './AccountContext';
import { isAdmin } from '../config/adminConfig';

const ItemsContext = createContext();

// export const getItemById = (id) => {
//     let category = 'coaching';
//     switch (parseInt(id,10) / 1000) {
//         case 8: category = 'concierge';
//         case 7: category = 'hospitality';
//         case 6: category = 'crafts';
//         case 5: category = 'academics';
//         case 4: category = 'publications';
//         case 3: category = 'gallery';
//         case 2: category = 'consulting';
//         case 1: category = 'software';
//         default:
//             console.error('invalid id');
//     }
//     const items = itemsByCategory[category];
//     return items.find(item => item.id == id);
// };

export const useItems = () => {
    const context = useContext(ItemsContext);
    if (!context) {
        throw new Error('useItems must be used within an ItemsProvider');
    }
    return context;
};

export const ItemsProvider = ({ children }) => {
    const { auth } = useAccountData();
    const showDevItems = isAdmin(auth?.email);

    const filterByEnv = useCallback((items) => {
        if (!Array.isArray(items)) return items;
        if (showDevItems) return items;
        return items.filter(item => (item.env || 'prod') === 'prod');
    }, [showDevItems]);

    const [servicesByCategory] = useState({
        "software": "Software Development",
        "academics": "Academics",
        "publications": "Publications",
        "gallery": "Gallery",
        "studio": "Studio",
        "crafts": "Crafts",
        "consulting": "Consulting",
        "hospitality": "Hospitality",
        "concierge": "Concierge",
        "coaching": "Coaching",
        "supply-chain": "Supply Chain",
        "family-friends": "Family & Friends"
    });
    
    const [filtersByCategory] = useState({
        software: [
        {
            icon: <Smartphone size={18} />,
            title: "Design",
            filter: "design"
        },
        {
            icon: <Code size={18} />,
            title: "Dev",
            filter: "dev"
        },
        {
            icon: <Database size={18} />,
            title: "R&D",
            filter: "r&d"
        }
        ],
        academics: [
            {
                icon: <GraduationCap size={18} />,
                title: "Courses",
                filter: "courses"
            },
            {
                icon: <BookOpen size={18} />,
                title: "Resources",
                filter: "resources"
            },
            {
                icon: <Users size={18} />,
                title: "Tutor",
                filter: "tutor"
            }
        ],
        publications: [
            {
                icon: <Book size={18} />,
                title: "Books",
                filter: "books"
            },
            {
                icon: <Newspaper size={18} />,
                title: "Articles",
                filter: "articles"
            },
            {
                icon: <PenLine size={18} />,
                title: "Content Creation",
                filter: "creation"
            },
            {
                icon: <SlidersHorizontal size={18} />,
                title: "Editorial Services",
                filter: "editorial"
            }
        ],
        gallery: [
            {
                icon: <Image size={18} />,
                title: "Landscape",
                filter: "landscape"
            },
            {
                icon: <User size={18} />,
                title: "Portrait",
                filter: "portrait"
            },
            {
                icon: <Palette size={18} />,
                title: "Commission",
                filter: "commission"
            },
            {
                icon: <Shirt size={18} />,
                title: "Print",
                filter: "print"
            },
            {
                icon: <Camera size={18} />,
                title: "Photography",
                filter: "bikepacking"
            }
        ],
        consulting: [
            {
                icon: <Lightbulb size={18} />,
                title: "Business",
                filter: "business"
            },
            {
                icon: <Handshake size={18} />,
                title: "Negotiation",
                filter: "negotiation"
            },
            {
                icon: <Users size={18} />,
                title: "HR",
                filter: "hr"
            }
        ],
        crafts: [
            {
                icon: <Home size={18} />,
                title: "Development",
                filter: "development"
            },
            {
                icon: <Hammer size={18} />,
                title: "Woodworks",
                filter: "woodworks"
            },
            {
                icon: <Wallet size={18} />,
                title: "Leatherworks",
                filter: "leatherworks"
            },
            {
                icon: <Flower size={18} />,
                title: "Bath&Body",
                filter: "bath&body"
            }
        ],
        hospitality: [
            {
                icon: <Calendar size={18} />,
                title: "Event",
                filter: "event"
            },
            {
                icon: <Utensils size={18} />,
                title: "Catering",
                filter: "catering"
            },
            {
                icon: <Building size={18} />,
                title: "Stay",
                filter: "stay"
            }
        ],
        concierge: [
            {
                icon: <UserCircle size={18} />,
                title: "Assistant",
                filter: "assistant"
            },
            {
                icon: <Compass size={18} />,
                title: "Guide",
                filter: "guide"
            },
            {
                icon: <Baby size={18} />,
                title: "Child Tender",
                filter: "child-tender"
            }
        ],
        coaching: [
            {
                icon: <Volleyball size={18} />,
                title: "Sports",
                filter: "sports"
            },
            {
                icon: <Dumbbell size={18} />,
                title: "Training",
                filter: "training"
            },
            {
                icon: <Heart size={18} />,
                title: "Wellness",
                filter: "wellness"
            }
        ],
        "supply-chain": [
            {
                icon: <Sprout size={18} />,
                title: "Farm & Agricultural",
                filter: "farm"
            },
            {
                icon: <Gem size={18} />,
                title: "Resources",
                filter: "resources"
            }
        ],
        "family-friends": [
            {
                icon: <UsersRound size={18} />,
                title: "Private Album",
                filter: "private-album"
            }
        ]
    });

    // Lazy-load: categories loaded on first access (reduces memory)
    const [itemsByCategory, setItemsByCategory] = useState({});
    const [loadingCategories, setLoadingCategories] = useState(new Set());
    const loadedRef = useRef(new Set());
    const loadingRef = useRef(new Set());
    const pendingRef = useRef(new Map());
    // Mirror of itemsByCategory as a ref so useCallback can read current data
    // without stale-closure issues (itemsByCategory state is async-committed).
    const itemsRef = useRef({});

    const itemsForCategory = (category) =>
        dedupeCatalogItemsById(filterByEnv(itemsByCategory[category] || []));

    const loadCategory = useCallback(async (category) => {
        // Cached path: read from itemsRef (always current, unlike state in memoized closure)
        if (loadedRef.current.has(category)) {
            const cached = itemsRef.current[category];
            if (Array.isArray(cached) && cached.length > 0) {
                return dedupeCatalogItemsById(filterByEnv(cached));
            }
            // State hasn't committed yet — reset ref and reload
            loadedRef.current.delete(category);
            // Fall through to loading path below
        }

        // Concurrent-call guard: wait for the in-flight promise
        if (loadingRef.current.has(category)) {
            const pending = pendingRef.current.get(category);
            if (pending) {
                // Return the promise result directly, not itemsForCategory(),
                // because itemsByCategory state may not have committed yet.
                return await pending;
            }
            return [];
        }

        // First call — kick off loading
        loadingRef.current.add(category);
        setLoadingCategories((prev) => new Set(prev).add(category));

        const promise = (async () => {
            const items = await loadCategoryModule(category);
            const parsed = await parseMarkdownForCategory(category, items, setItemsByCategory, ALL_MARKDOWN_FILES);
            const final = dedupeCatalogItemsById(filterByEnv(parsed));
            // Write to both state (for re-render) and ref (for immediate cache reads)
            itemsRef.current = { ...itemsRef.current, [category]: final };
            loadedRef.current.add(category);
            return final;
        })();

        pendingRef.current.set(category, promise);
        try {
            return await promise;
        } finally {
            loadingRef.current.delete(category);
            pendingRef.current.delete(category);
            setLoadingCategories((prev) => {
                const next = new Set(prev);
                next.delete(category);
                return next;
            });
        }
    }, [filterByEnv]);

    const loadAllCategories = useCallback(async () => {
        const keys = getCategoryKeys();
        await Promise.all(keys.map((cat) => loadCategory(cat)));
    }, [loadCategory]);

    const getItemById = (id) => {
        const category = getCategoryFromId(id);
        const items = itemsForCategory(category);
        return items.find(item => item.id === id);
    };

    const getItemByPath = useCallback((category, shortTitle) => {
        const items = itemsForCategory(category);
        return items.find(item => item.shortTitle === shortTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsByCategory, filterByEnv]);


    const getItemPathByShortTitle = (category, shortTitle) => {
        const items = itemsForCategory(category);
        const item = items.find((i) => i.shortTitle === shortTitle);
        return item?.contentPath;
    };
    const getItemsByCategory = (category) => {
        return itemsForCategory(category);
    };

    const getFiltersByCategory = (category) => {
        return filtersByCategory[category] || [];
    };

    const getItemsByFilter = (category, filter) => {
        const items = itemsForCategory(category);
        return filterCatalogItemsBySubFilter(items, filter);
    };

    const getServicesByCategory = (category) => {
        return servicesByCategory[category] || [];
    };

    // Flatten all items across categories – useful for backend seeding scripts
    const getAllItems = () => {
        const allItems = [];
        Object.keys(itemsByCategory).forEach(category => {
            allItems.push(...itemsForCategory(category));
        });
        return allItems;
    };

    const syncItemsToBackend = async (token) => {
        await loadAllCategories();
        const allItems = getAllItems();
        return await syncItemsToBackendUtil(allItems, token);
    };

    const parseMarkdownToItems = async () => {
        await loadAllCategories();
        return { success: true, message: 'Categories loaded and markdown parsed.' };
    };

    const isCategoryLoading = (category) => loadingCategories.has(category);

    return (
        <ItemsContext.Provider value={{
            itemsByCategory,
            getItemById,
            getItemByPath,
            getItemPathByShortTitle,
            getItemsByCategory,
            getFiltersByCategory,
            getItemsByFilter,
            getServicesByCategory,
            getAllItems,
            loadCategory,
            loadAllCategories,
            isCategoryLoading,
            syncItemsToBackend,
            parseMarkdownToItems,
            showDevItems,
            filterByEnv,
            getCategoryFromId
        }}>
            {children}
        </ItemsContext.Provider>
    );
};

export default ItemsProvider;
